/**
 * Where the visitor was trying to get to before the membership gate stopped them.
 *
 * Event pages are meant to be pasted into WhatsApp, and the person who taps that link is
 * quite often not signed in on this device. Both ways in throw the path away: Google is sent
 * `redirectTo: window.location.origin`, and the dev-login endpoint hands gotrue a fixed
 * `redirect_to`. Either way they come back to the board with no idea what they clicked.
 *
 * Fixing it at the redirect would mean every deep link having to be in Supabase's redirect
 * allow-list — a wildcard on the auth config, changed in a dashboard, to solve a problem the
 * browser already has the state for. So the path is kept here instead, across the round trip,
 * and spent once the gate opens.
 *
 * `sessionStorage`, not `localStorage`: it belongs to this tab and this visit. The OAuth
 * round trip returns to the same tab, so it survives exactly as long as it needs to and
 * cannot leak a stale destination into a window opened tomorrow. The cost is a magic link
 * opened in a different tab from the one that asked for it, which lands on the events list —
 * the front door, not a new failure.
 *
 * With no deep link at all, signing in still goes somewhere on purpose: the events list.
 */

const KEY = "rally:return-to";

/** Only in-app paths. A stored absolute URL would be an open redirect wearing a hat. */
function isSafePath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//");
}

/**
 * Where somebody signing in from the front door lands. The events, not the board: signing in
 * is nearly always about entering something.
 */
const AFTER_SIGN_IN = "/events";

export function rememberReturnTo(path: string): void {
  if (!isSafePath(path)) return;
  try {
    // The front door only fills an empty slot. Google and the magic link both come back to
    // "/", and that return can pass through signed-out before the session is read — it must
    // not overwrite the deep link the visitor actually left from.
    if (path === "/") {
      if (sessionStorage.getItem(KEY) === null) sessionStorage.setItem(KEY, AFTER_SIGN_IN);
      return;
    }
    sessionStorage.setItem(KEY, path);
  } catch {
    // Private mode, or storage full. A deep link that lands on the board is a worse
    // morning than one that does not, but it is not worth a broken render.
  }
}

/** The stored path, cleared as it is read — it is only ever spent once. */
export function takeReturnTo(): string | null {
  try {
    const path = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return path && isSafePath(path) ? path : null;
  } catch {
    return null;
  }
}
